import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Field, GoldButton, NativeSelect } from "@/components/atlas/Fields";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { usePositions } from "@/lib/positions";
import { useLang, errorText } from "@/lib/i18n";
import { NotificationPrefs } from "@/components/employees/NotificationPrefs";

export default function Profile() {
  const { t, setLang } = useLang();
  const { user, setUser, signOut } = useAuth();
  const { label } = usePositions();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [areas, setAreas] = useState("");
  const [lang, setL] = useState("fr");

  useEffect(() => {
    setPhone(user.phone || "");
    setAreas((user.preferred_areas || []).join(", "));
    setL(user.lang || "fr");
  }, [user]);

  const save = async () => {
    try {
      const r = await api.put("/me/profile", { phone, preferred_areas: areas.split(",").map((s) => s.trim()).filter(Boolean), lang });
      setUser(r.data);
      setLang(lang);
      toast.success(t("c.saved"));
    } catch (e) {
      toast.error(errorText(t, e));
    }
  };

  return (
    <div>
      <div className="mb-8 rise">
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" data-testid="page-title">{t("p.profileTitle")}</h1>
        <p className="text-sm text-neutral-500 mt-3">{t("p.profileSub")}</p>
      </div>
      <div className="card-lux p-6 sm:p-8 rise space-y-6">
        <div>
          <div className="font-display text-3xl font-semibold" data-testid="profile-name">{user.name}</div>
          <div className="text-sm text-neutral-500 mt-1" data-testid="profile-email">{user.email}</div>
        </div>
        <div>
          <div className="eyebrow mb-2">{t("emp.qualified")}</div>
          <div className="flex flex-wrap gap-2" data-testid="profile-positions">
            {(user.positions || []).map((p) => (
              <span key={p} className={`text-xs px-3 py-1.5 border ${p === user.primary_position ? "border-[#C5A059] bg-[#C5A059]/10" : "border-black/10"}`}>{label(p)}</span>
            ))}
          </div>
          <p className="text-[11px] text-neutral-400 mt-2">{t("p.qualifiedNote")}</p>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label={t("c.phone")}><Input data-testid="profile-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} className="h-11 rounded-none" /></Field>
          <Field label={t("emp.lang")}>
            <NativeSelect testid="profile-lang-select" value={lang} onChange={setL} className="h-11"><option value="fr">Français</option><option value="en">English</option></NativeSelect>
          </Field>
          <Field label={t("emp.areas")} className="sm:col-span-2"><Input data-testid="profile-areas-input" value={areas} onChange={(e) => setAreas(e.target.value)} className="h-11 rounded-none" /></Field>
        </div>
        <GoldButton data-testid="profile-save-btn" onClick={save} className="w-full sm:w-auto h-11">{t("c.save")}</GoldButton>
      </div>
      <NotificationPrefs />
      <div className="flex items-start gap-2 text-xs text-neutral-500 mt-6 px-1">
        <ShieldCheck className="w-4 h-4 text-[#C5A059] shrink-0" />{t("p.privacy")}
      </div>
      <button data-testid="employee-logout-btn" onClick={() => { signOut(); navigate("/login"); }} className="press mt-6 flex items-center gap-2 text-sm text-neutral-500 hover:text-black">
        <LogOut className="w-4 h-4" />{t("c.logout")}
      </button>
    </div>
  );
}
